<template>
	<BlockVisibilityObserver title="HOW_IT_WORKS">
		<section class="kova-lp-steps">
			<div class="kova-lp-steps__inner">
				<div class="kova-lp-steps__copy">
					<h2 class="kova-lp-steps__title">
						<span
							v-for="(part, index) in titleParts"
							:key="index"
							:class="{ 'kova-lp-steps__title_accent': part.accent }"
							v-text="part.text"
						/>
					</h2>
					<p class="kova-lp-steps__subtitle">{{ subtitle }}</p>
				</div>

				<ol class="kova-lp-steps__list">
					<li v-for="(step, index) in steps" :key="step.title" class="kova-lp-steps__step">
						<span class="kova-lp-steps__marker" :class="{ 'kova-lp-steps__marker_active': index === 0 }">
							{{ index + 1 }}
						</span>
						<span class="kova-lp-steps__step-title">{{ step.title }}</span>
					</li>
				</ol>

				<div
					class="kova-lp-steps__package"
					:class="{ 'kova-lp-steps__package_baked': packageShot?.baked }"
					:style="packageStyle"
				>
					<span class="kova-lp-steps__package-frame">
						<picture class="kova-lp-steps__package-picture">
							<source v-if="!packageShot" media="(min-width: 1024px)" :srcset="packageDesktop" />
							<img alt="" class="kova-lp-steps__package-img" :src="packageShot?.image ?? packageMobile" />
						</picture>
					</span>
					<p class="kova-lp-steps__disclaimer">{{ disclaimer }}</p>
				</div>

				<button class="kova-lp-steps__cta" type="button" @click="onCtaClick">{{ ctaLabel }}</button>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import packageDesktop from '@/assets/kova/landing/package-box-desktop.jpg'
import packageMobile from '@/assets/kova/landing/package-box-mobile.png'

export default {
	name: 'LandingKovaSteps',

	components: { BlockVisibilityObserver },

	props: {
		ctaLabel: {
			type: String,
			required: true,
		},

		disclaimer: {
			type: String,
			required: true,
		},

		// { image, baked, mobileRatio, desktop: { top, height, overhang, captionOverlap } } —
		// one shot for both breakpoints, composed for another frame. Null keeps the base
		// frames (5026:7574 / 5026:7326) and the pair shipped with the component.
		packageShot: {
			type: Object,
			default: null,
		},

		// [{ title }] — the numbered timeline.
		steps: {
			type: Array,
			required: true,
		},

		subtitle: {
			type: String,
			required: true,
		},

		titleParts: {
			type: Array,
			required: true,
		},
	},

	emits: ['progressLoaderCompleted'],

	setup() {
		return { packageDesktop, packageMobile }
	},

	computed: {
		// Unset properties fall back to the base frame in the stylesheet.
		packageStyle() {
			if (!this.packageShot) {
				return null
			}
			const { mobileRatio, desktop } = this.packageShot
			return {
				'--kova-lp-steps-package-ratio': mobileRatio,
				'--kova-lp-steps-package-top': `${desktop.top}px`,
				'--kova-lp-steps-package-height': `${desktop.height}px`,
				'--kova-lp-steps-package-overhang': `${desktop.overhang}px`,
				'--kova-lp-steps-caption-overlap': `${desktop.captionOverlap}px`,
			}
		},
	},

	methods: {
		onCtaClick() {
			this.$emit('progressLoaderCompleted', 'how_it_works_eligible')
		},
	},
}
</script>

<style lang="scss" scoped>
// Mobile (Figma 5026:7574): one column — copy, timeline, then the package shot.
.kova-lp-steps {
	font-family: var(--kova-lp-font);
	background: var(--kova-lp-page);
}

.kova-lp-steps__inner {
	display: flex;
	flex-direction: column;
	gap: var(--size-32);
	padding-top: var(--size-40);
}

.kova-lp-steps__copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-16);
	padding-inline: var(--size-20);

	text-align: center;
}

.kova-lp-steps__title {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: 1.12;
	letter-spacing: -0.64px;

	color: var(--kova-lp-text);
	text-wrap: balance;
}

.kova-lp-steps__title_accent {
	color: var(--kova-lp-highlight);
}

.kova-lp-steps__subtitle {
	margin: 0;

	font-size: var(--size-16);
	line-height: 1.5;

	color: var(--kova-lp-brand);
}

.kova-lp-steps__list {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	margin: 0;
	padding: 0 var(--size-20);

	list-style: none;
}

.kova-lp-steps__step {
	position: relative;
	display: flex;
	gap: var(--size-12);
}

// The dashed rail runs from each marker down to the next one (Line 4/5 in Figma
// are a plain 2px dash, so no asset is needed).
.kova-lp-steps__step:not(:last-child)::before {
	content: '';
	position: absolute;
	left: 13px;
	top: var(--size-26);
	bottom: calc(-1 * var(--size-24) - 1px);

	border-left: 2px dashed var(--kova-lp-hairline-soft);
}

.kova-lp-steps__marker {
	box-sizing: border-box;
	z-index: 1;

	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: var(--size-28);
	height: var(--size-28);

	font-family: var(--font-family);
	font-size: var(--size-14);
	font-weight: 700;
	line-height: var(--size-20);

	border-radius: var(--size-32);
	color: var(--Text-Primary-60);
	background: var(--Fill-Default-Full);
}

.kova-lp-steps__marker_active {
	border: 4px solid var(--kova-lp-highlight-ring);
	color: var(--Fill-White);
	background: var(--kova-lp-highlight);
}

.kova-lp-steps__step-title {
	min-width: 0;

	font-size: var(--size-18);
	font-weight: 500;
	line-height: 1.4;
	letter-spacing: -0.18px;

	color: var(--kova-lp-text);
}

.kova-lp-steps__package {
	display: flex;
	flex-direction: column;
	gap: var(--size-4);
	padding-inline: var(--size-20);
}

// The shot is bled to the frame edges and faded into the page at the bottom.
//
// The frame keeps the phone's 375x285 proportions instead of pinning the height:
// pinned, it grew wider without growing taller, and past ~402px `cover` had to start
// cropping vertically — which cuts the top off the box (the export has no slack
// above it). Held to the ratio, the crop stays horizontal, where the shot has margin
// on both sides. At 375 this is the same 375x285 frame as before.
.kova-lp-steps__package-frame {
	position: relative;
	overflow: hidden;

	display: block;
	aspect-ratio: var(--kova-lp-steps-package-ratio, 375 / 285);
	margin-inline: calc(-1 * var(--size-20));
}

.kova-lp-steps__package-frame::after {
	content: '';
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: var(--size-52);

	background: linear-gradient(to bottom, var(--kova-lp-page-clear), var(--kova-lp-page));
}

// A shot exported with its fades already in it would get them twice.
.kova-lp-steps__package_baked .kova-lp-steps__package-frame::after {
	display: none;
}

.kova-lp-steps__package-picture {
	display: contents; // carries the desktop <source> only, adds no box
}

// Each export is already cropped to its own frame, so the shot just fills it —
// no oversized placement, and no blur: the earlier low-res asset needed one to
// hide its upscaling, these do not.
.kova-lp-steps__package-img {
	position: absolute;
	inset: 0;

	width: 100%;
	height: 100%;
	max-width: none;

	object-fit: cover;
}

.kova-lp-steps__disclaimer {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-12);
	line-height: var(--size-16);

	color: var(--kova-lp-muted);
	text-align: center;
}

// Mobile has no CTA in this block (5026:7574) — it lives in the next one.
.kova-lp-steps__cta {
	display: none;
}

// Desktop (Figma 5026:7327): package shot on the left, copy + timeline + CTA right.
@media (min-width: 1024px) {
	// This section keeps Figma's 120px gutter at every width rather than capping the
	// content: the package shot bleeds out through that gutter to the page edge, and
	// a fixed gutter is what lets the bleed stay exact instead of chasing the cap.
	.kova-lp-steps__inner {
		position: relative;
		box-sizing: border-box;
		max-width: 1440px;
		margin-inline: auto;
		padding: var(--size-80) var(--size-120) var(--size-8);

		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr)); // 584 / 584 in the frame
		gap: var(--size-40) var(--size-32);
		justify-content: center;
		align-items: start;
	}

	.kova-lp-steps__copy {
		grid-column: 2;
		gap: var(--size-24);
		padding-inline: 0;

		text-align: left;
	}

	.kova-lp-steps__title {
		font-size: var(--size-56);
		line-height: 1.1;
		letter-spacing: -1.12px;

		text-wrap: initial;
	}

	.kova-lp-steps__subtitle {
		font-size: var(--size-20);

		color: var(--kova-lp-text);
	}

	.kova-lp-steps__list {
		grid-column: 2;
		gap: var(--size-24);
		padding: 0;
	}

	.kova-lp-steps__step-title {
		font-size: var(--size-20);
		letter-spacing: -0.2px;
	}

	// The shot spans both left-column rows and bleeds past the 120px page gutter.
	.kova-lp-steps__package {
		grid-column: 1;
		grid-row: 1 / span 3;
		gap: 0;
		padding-inline: 0;
	}

	// The base frame is 688 tall, top-aligned and stops at its column (5026:7326 — a frame
	// size, past the end of the size ladder); packageShot can move, resize and widen it.
	.kova-lp-steps__package-frame {
		height: var(--kova-lp-steps-package-height, 688px);
		margin-top: var(--kova-lp-steps-package-top, 0px);
		// Out through the page gutter on the left; an overhang runs on under the copy column.
		margin-inline: calc(-1 * var(--size-120)) calc(-1 * var(--kova-lp-steps-package-overhang, 0px));

		background: var(--kova-lp-page);
	}

	// The desktop export bakes the fade in — its bottom row is already #F7F4EF — so
	// the CSS overlay would just repeat it. Mobile still ships a cut-out and keeps it.
	.kova-lp-steps__package-frame::after {
		display: none;
	}

	// `cover` had to crop the 1600x1376 export sideways to fill the 704x688 frame,
	// which cut the shot visibly. `contain` fits the whole thing; the letterbox is
	// invisible because the export's own background is the page colour the frame
	// already paints.
	.kova-lp-steps__package-img {
		object-fit: contain;
	}

	// A second fade down the right edge, so the shot dissolves into the copy column.
	.kova-lp-steps__package-frame::before {
		content: '';
		position: absolute;
		right: 0;
		top: 0;
		bottom: 0;
		z-index: 1;
		width: 200px;

		background: linear-gradient(to right, var(--kova-lp-page-clear), var(--kova-lp-page));
	}

	.kova-lp-steps__package_baked .kova-lp-steps__package-frame::before {
		display: none;
	}

	// The copy column paints over a shot that overhangs into it, as in the frame.
	.kova-lp-steps__copy,
	.kova-lp-steps__list,
	.kova-lp-steps__cta {
		position: relative;
		z-index: 3;
	}

	// Figma sits the caption over the faded bottom of the shot rather than under it
	// (5026:7326 starts 64px before the package frame ends). `position` is what makes
	// that work: the frame it overlaps is relative with an opaque page-colour fill, so
	// a static caption paints underneath it and disappears. `z-index` clears the frame's
	// right-edge fade too — that one is ::before at z-index 1, and it would wash the tail
	// of the sentence out; in the frame the caption reads end to end.
	.kova-lp-steps__disclaimer {
		position: relative;
		z-index: 2;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: center;
		height: 90px;
		margin-top: calc(-1 * var(--kova-lp-steps-caption-overlap, var(--size-64)));
		padding: var(--size-8) var(--size-12);

		font-size: var(--size-14);
		line-height: var(--size-20);
	}

	.kova-lp-steps__cta {
		box-sizing: border-box;
		grid-column: 2;

		display: flex;
		align-items: center;
		justify-content: center;
		width: 368px;
		max-width: 100%;
		height: 90px; // 5026:7326 — between --size-88 and --size-96, kept exact
		padding: 0 var(--size-24);

		font-family: inherit;
		font-size: var(--size-24);
		font-weight: 500;
		line-height: normal;

		border: none;
		border-radius: var(--size-28);
		color: var(--Fill-White);
		background: var(--kova-lp-brand);
		cursor: pointer;
		white-space: nowrap;
	}
}
</style>
